/**
 * components/search/CommandPalette.jsx — Iter 49 Universal Plan Search.
 *
 * Global Cmd/Ctrl + K overlay. Field-first: opens flush, single input,
 * typed results, ranked. Tap result → dispatchJump → Iter 48 navigator
 * handles same-sheet / cross-sheet routing. Closes on Esc or backdrop.
 *
 * Renders absolutely nothing until the user opens it — zero idle cost.
 */
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Loader2, Layers3, MapPin, GitBranch, Ruler, Eye,
  AlertCircle, Camera, Briefcase, FileText, ChevronRight, Route, Navigation2,
  AlertTriangle, FileCheck, Pencil, Share2, FileDown, User as UserIcon, ClipboardCheck,
  Lightbulb, BookOpen, Package as PackageIcon, ExternalLink, Lock,
} from "lucide-react";
import { api } from "@/lib/api";
import { dispatchJump } from "@/lib/jumpTo";
import { safeExternalHref } from "@/lib/utils";

const TYPE_ICON = {
  structure: Layers3, pin: MapPin, revision: FileText,
  measurement: Ruler, utility_watch: Eye, project: Briefcase,
  issue: AlertCircle, photo: Camera,
  alignment: Route, station_point: Navigation2,
  pipe: GitBranch, conflict: AlertTriangle,
  document: FileCheck,
  // Iter 82.1
  markup: Pencil,
  share: Share2,
  export: FileDown,
  user: UserIcon,
  // Iter 82.2
  punch: ClipboardCheck,
  // Iter 109
  spec_section: BookOpen,
  intake_document: FileCheck,
  core_reference: ExternalLink,
  // Iter 110 — find finding alias
  discovery: Lightbulb,
};

// Iter 110 Phase 2 — visual category color per result type (matches the
// tone of the source surface so a user recognises the hit at a glance).
const TYPE_TONE = {
  revision: { dot: "var(--fop-primary)",     pill: "rgba(232,93,31,0.12)",  pillText: "var(--fop-primary)" },
  spec_section: { dot: "rgb(99,179,237)",    pill: "rgba(99,179,237,0.12)", pillText: "rgb(99,179,237)" },
  intake_document: { dot: "rgb(180,180,180)", pill: "rgba(180,180,180,0.10)", pillText: "var(--fop-text-2)" },
  core_reference: { dot: "rgb(247,144,9)",   pill: "rgba(247,144,9,0.15)",  pillText: "rgb(247,144,9)" },
  punch:   { dot: "rgb(229,115,115)",        pill: "rgba(229,115,115,0.12)", pillText: "rgb(229,115,115)" },
  discovery: { dot: "rgb(250,204,21)",       pill: "rgba(250,204,21,0.15)",  pillText: "rgb(250,204,21)" },
  photo:   { dot: "rgb(170,128,255)",        pill: "rgba(170,128,255,0.12)", pillText: "rgb(170,128,255)" },
};

const TYPE_LABEL = {
  structure: "Structure", pin: "Pin", revision: "Sheet",
  measurement: "Measurement", utility_watch: "Utility Watch",
  project: "Project", issue: "Issue", photo: "Photo",
  alignment: "Alignment", station_point: "Station",
  pipe: "Pipe Run", conflict: "Utility Conflict",
  document: "Document",
  // Iter 82.1
  markup: "Markup",
  share: "Plan Share",
  export: "Plan Export",
  user: "User",
  // Iter 82.2
  punch: "Punch Item",
  // Iter 109
  spec_section: "Spec Section",
  intake_document: "Document",
  core_reference: "CORE",
  // Iter 110
  discovery: "Field Finding",
};

const MIN_QUERY = 2;
const DEBOUNCE_MS = 200;

// Iter 115 P1 — Knowledge Engine response flattener.
// Converts the grouped /api/search/global response into the existing
// flat hit shape the CommandPalette UI already renders, so no downstream
// component has to change. Order preserved (plans → spec_sections →
// intake → findings → core_references → others).
const GROUP_ORDER = [
  "plans", "spec_sections", "intake", "findings",
  "core_references", "photos", "observations", "daily_reports",
];
function flattenGlobalSearch(data) {
  const groups = data?.groups || {};
  const out = [];
  for (const g of GROUP_ORDER) {
    const rows = groups[g] || [];
    for (const r of rows) {
      switch (g) {
        case "plans":
          out.push({ object_type: "revision", object_id: r.id, sheet_id: r.id, project_id: r.project_id,
            label: `${r.sheet_number || ""}${r.sheet_title ? " · " + r.sheet_title : ""}`.trim() || r.id,
            match_reason: r.match_reason });
          break;
        case "spec_sections":
          out.push({ object_type: "spec_section", object_id: r.id, project_id: r.project_id,
            label: `${r.section_number || ""}${r.section_title ? " · " + r.section_title : ""}`.trim() || r.id,
            sublabel: r.division_title, match_reason: r.match_reason });
          break;
        case "intake":
          out.push({ object_type: "intake_document", object_id: r.id, project_id: r.project_id,
            label: r.file_name || r.id, sublabel: r.intake_category, match_reason: r.match_reason });
          break;
        case "findings":
          out.push({ object_type: "discovery", object_id: r.id, project_id: r.project_id,
            label: r.title || r.id, sheet_id: r.sheet_id,
            sublabel: r.sheet_number ? `Sheet ${r.sheet_number}` : null,
            match_reason: r.match_reason });
          break;
        case "core_references":
          out.push({ object_type: "core_reference", object_id: r.id, project_id: r.project_id,
            label: r.title || r.id, sublabel: r.core_record_type, url: r.url,
            match_reason: r.match_reason });
          break;
        case "photos":
          out.push({ object_type: "photo", object_id: r.id, project_id: r.project_id,
            label: r.caption || "Photo", match_reason: r.match_reason });
          break;
        case "observations":
          out.push({ object_type: "issue", object_id: r.id, project_id: r.project_id,
            label: r.title || r.description || r.id, match_reason: r.match_reason });
          break;
        case "daily_reports":
          out.push({ object_type: "document", object_id: r.id, project_id: r.project_id,
            label: r.summary || `Daily ${r.report_date || ""}`.trim(),
            match_reason: r.match_reason });
          break;
        default: break;
      }
    }
  }
  return out;
}

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global Cmd/Ctrl+K listener
  useEffect(() => {
    const onKey = (e) => {
      const isMod = e.metaKey || e.ctrlKey;
      if (isMod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Allow other components (e.g. a header search button) to open the palette
  useEffect(() => {
    const fn = () => setOpen(true);
    window.addEventListener("forgedops:open-search", fn);
    return () => window.removeEventListener("forgedops:open-search", fn);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setIdx(0);
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
    // reset on close so next open is fresh
    setQ("");
    setResults([]);
    setErr(null);
  }, [open]);

  // Debounced search — Iter 115 P1 (Defect C):
  // Hits the Knowledge Engine endpoint /search/global which is the
  // single source of truth (replaces the legacy single-token /search).
  // Returns grouped results across plans / spec_sections / intake /
  // findings / core_references / photos / observations / daily_reports.
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < MIN_QUERY) {
      setResults([]); setErr(null); setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search/global", {
          params: { q: trimmed, limit_per_group: 10 },
        });
        if (cancelled) return;
        const flat = flattenGlobalSearch(data);
        setResults(flat);
        setErr(null);
      } catch (e) {
        if (cancelled) return;
        setResults([]);
        if (!window.navigator?.onLine || /Network|timeout/i.test(e?.message || "")) {
          setErr("Offline search limited to cached plans.");
        } else {
          setErr("Search failed. Try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  // Keyboard nav
  const activate = useCallback((hit) => {
    setOpen(false);
    // Project → route to portfolio→project shell
    if (hit.object_type === "project") {
      navigate(`/project/${hit.object_id}`);
      return;
    }
    // Iter 50 — STA-text synthesised station point. Jump to (sheet, nx, ny)
    // without naming an object_id (camera-only navigation).
    if (hit.object_type === "station_point") {
      if (hit.sheet_id) {
        dispatchJump({
          object_type: "station_point",
          object_id: hit.object_id,
          sheet_id: hit.sheet_id,
          normalized_x: hit.normalized_x,
          normalized_y: hit.normalized_y,
          label: hit.label,
        });
        navigate(`/sheet/${hit.sheet_id}`);
      }
      return;
    }
    // Iter 52 — document hits: jump to OWNING object and open its
    // Documents tab. We pipe `target_tab` through dispatchJump and the
    // SheetViewer's jump handler reads it.
    if (hit.object_type === "document") {
      if (hit.jump_object_type && hit.jump_object_id) {
        dispatchJump({
          object_type: hit.jump_object_type,
          object_id: hit.jump_object_id,
          sheet_id: hit.sheet_id,
          normalized_x: hit.normalized_x,
          normalized_y: hit.normalized_y,
          label: hit.label,
          target_tab: "documents",
          focus_document_id: hit.object_id,
        });
        if (hit.sheet_id) navigate(`/sheet/${hit.sheet_id}`);
      }
      return;
    }
    // Issue / Photo → jump to the OWNING object (jump_object_type/id)
    if (hit.jump_object_type && hit.jump_object_id) {
      dispatchJump({
        object_type: hit.jump_object_type,
        object_id: hit.jump_object_id,
        sheet_id: hit.sheet_id,
        normalized_x: hit.normalized_x,
        normalized_y: hit.normalized_y,
        label: hit.label,
      });
      if (hit.sheet_id) navigate(`/sheet/${hit.sheet_id}`);
      return;
    }
    // Sheet/revision → navigate to that sheet
    if (hit.object_type === "revision" && hit.object_id) {
      navigate(`/sheet/${hit.object_id}`);
      return;
    }
    // Iter 115 P1 — knowledge hits route to the surface that owns them.
    if (hit.object_type === "spec_section" && hit.object_id) {
      navigate(`/spec-section/${hit.object_id}`);
      return;
    }
    if (hit.object_type === "intake_document" && hit.object_id) {
      navigate(`/intake?focus=${hit.object_id}`);
      return;
    }
    if (hit.object_type === "discovery" && hit.object_id) {
      // Land on Findings list — viewer/editor opens from there.
      navigate(`/discoveries?focus=${hit.object_id}`);
      return;
    }
    if (hit.object_type === "core_reference") {
      // CORE stubs link to the external system; open in a new tab if a
      // URL is present, otherwise fall through to no-op.
      // Security hardening (2026-09-08, SEC-001): `window.open` on a raw
      // customer-controlled URL can still execute a javascript: URI in
      // some browsers -- sanitize before ever handing it to window.open.
      const safe = safeExternalHref(hit.url);
      if (safe) { window.open(safe, "_blank", "noopener"); return; }
      return;
    }
    // Structure/pin/measurement/utility_watch/alignment — dispatch jump + nav.
    dispatchJump({
      object_type: hit.object_type,
      object_id: hit.object_id,
      sheet_id: hit.sheet_id,
      normalized_x: hit.normalized_x,
      normalized_y: hit.normalized_y,
      label: hit.label,
    });
    if (hit.sheet_id) navigate(`/sheet/${hit.sheet_id}`);
  }, [navigate, setOpen]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIdx((i) => Math.min(i + 1, Math.max(results.length - 1, 0)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && results[idx]) {
        e.preventDefault();
        activate(results[idx]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, idx, activate]);

  const grouped = useMemo(() => groupByType(results), [results]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center pt-24 pb-6 px-4"
      data-testid="command-palette"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            aria-hidden="true"
            onMouseDown={() => setOpen(false)} />

      <div
        className="relative w-full max-w-[640px] bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded-md shadow-2xl flex flex-col max-h-[70vh]"
        role="dialog" aria-label="Universal plan search"
        data-testid="command-palette-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-[var(--fop-border)]">
          <Search size={16} className="text-[var(--fop-text-3)] shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search sheets, plans, projects, photos, daily reports…  (min 2 chars)"
            className="flex-1 bg-transparent text-sm focus:outline-none text-[var(--fop-text)] placeholder:text-[var(--fop-text-4)]"
            data-testid="command-palette-input"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 size={14} className="animate-spin text-[var(--fop-text-3)]" />}
          <kbd className="hidden sm:inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] px-1.5 py-0.5 rounded border border-[var(--fop-border)]">
            Esc
          </kbd>
          <button
            onClick={() => setOpen(false)}
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] -mr-1"
            aria-label="Close search"
            data-testid="command-palette-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {err && (
            <div className="px-4 py-6 text-center text-xs text-[var(--fop-text-3)]"
                  data-testid="command-palette-error">
              {err}
            </div>
          )}
          {!err && q.trim().length < MIN_QUERY && (
            <div className="px-4 py-8 text-center text-xs text-[var(--fop-text-3)] space-y-1">
              <div>Type at least {MIN_QUERY} characters to search.</div>
              <div className="text-[10px] uppercase font-mono tracking-wider text-[var(--fop-text-4)]">
                Cmd / Ctrl + K · ↑↓ to navigate · ⏎ to jump
              </div>
            </div>
          )}
          {!err && q.trim().length >= MIN_QUERY && !loading && results.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-[var(--fop-text-3)]"
                  data-testid="command-palette-empty">
              No results. Try a different spelling or partial label.
            </div>
          )}
          {!err && results.length > 0 && (
            <ol data-testid="command-palette-results">
              {results.map((hit, i) => {
                const Icon = TYPE_ICON[hit.object_type] || ChevronRight;
                const tone = TYPE_TONE[hit.object_type];
                const isReadOnly = hit.extra?.read_only === true;
                const active = i === idx;
                return (
                  <li key={`${hit.object_type}:${hit.object_id}:${i}`}>
                    <button
                      onClick={() => activate(hit)}
                      onMouseEnter={() => setIdx(i)}
                      data-testid={`command-result-${hit.object_type}-${hit.object_id}`}
                      data-active={active ? "true" : "false"}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                        active
                          ? "bg-[var(--fop-surface-2)] text-[var(--fop-text)]"
                          : "text-[var(--fop-text-2)] hover:bg-[var(--fop-surface-2)]/60"
                      }`}
                    >
                      <Icon size={14} className="shrink-0" style={{ color: tone?.dot || "var(--fop-primary)" }} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium truncate flex items-center gap-1.5">
                          <span className="truncate">{hit.label || hit.object_id}</span>
                          {isReadOnly && (
                            <span
                              data-testid="cmd-readonly-pill"
                              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider"
                              style={{ background: tone?.pill, color: tone?.pillText, border: `1px solid ${tone?.pillText || 'var(--fop-border)'}` }}
                              title="Owned by MASCI CORE — view-only inside ForgedOps Plans"
                            >
                              <Lock size={8} /> Read-only
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] truncate flex items-center gap-1.5">
                          {tone && (
                            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
                          )}
                          <span style={{ color: tone?.pillText }}>{TYPE_LABEL[hit.object_type] || hit.object_type}</span>
                          {hit.object_type === "core_reference" && hit.extra?.url && (
                            <span className="text-[var(--fop-text-4)] inline-flex items-center gap-0.5">
                              · <ExternalLink size={9} /> View in CORE
                            </span>
                          )}
                          {renderSubline(hit)}
                          {hit.match_reason && (
                            <span
                              data-testid={`cmd-match-reason-${hit.object_id}`}
                              className="ml-auto pl-2 text-[10px] font-mono uppercase tracking-wider text-[var(--fop-primary)] shrink-0"
                              title={hit.match_reason}
                            >
                              {hit.match_reason}
                            </span>
                          )}
                        </div>
                      </div>
                      {active && (
                        <kbd className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] px-1.5 py-0.5 rounded border border-[var(--fop-border)]">
                          ⏎
                        </kbd>
                      )}
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>

        <div className="px-3 py-1.5 border-t border-[var(--fop-border)] text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] flex items-center justify-between">
          <span>{results.length} {results.length === 1 ? "result" : "results"}</span>
          <span className="hidden sm:inline">↑↓ navigate · ⏎ jump · Esc close</span>
        </div>
      </div>
    </div>
  );
}

function groupByType(rows) {
  const out = {};
  for (const r of rows) {
    (out[r.object_type] = out[r.object_type] || []).push(r);
  }
  return out;
}

function renderSubline(hit) {
  const x = hit.extra || {};
  const bits = [];
  // Iter 116 P2 — knowledge result sublabels.
  if (hit.object_type === "spec_section") {
    if (hit.sublabel) bits.push(hit.sublabel);
  } else if (hit.object_type === "intake_document") {
    if (hit.sublabel) bits.push(String(hit.sublabel).replace(/_/g, " ").toLowerCase());
  } else if (hit.object_type === "discovery") {
    if (hit.sublabel) bits.push(hit.sublabel);
  } else if (hit.object_type === "core_reference") {
    if (hit.sublabel) bits.push(String(hit.sublabel).replace(/_/g, " ").toLowerCase());
  } else if (hit.object_type === "project") {
    if (x.project_number) bits.push(x.project_number);
    if (x.status) bits.push(x.status);
  } else if (hit.object_type === "revision") {
    if (x.discipline) bits.push(x.discipline);
    if (x.is_current) bits.push("current");
  } else if (hit.object_type === "structure") {
    if (x.install_status) bits.push(x.install_status);
    if (x.conflict_state && x.conflict_state !== "normal") bits.push(x.conflict_state);
  } else if (hit.object_type === "pin") {
    if (x.status) bits.push(x.status);
    if (x.category) bits.push(x.category);
  } else if (hit.object_type === "measurement") {
    if (x.kind) bits.push(x.kind);
    if (x.value != null) bits.push(`${x.value}`);
  } else if (hit.object_type === "utility_watch") {
    if (x.layer_type) bits.push(x.layer_type);
  } else if (hit.object_type === "issue") {
    if (x.owner_label) bits.push(`on ${x.owner_label}`);
    if (x.status) bits.push(x.status);
  } else if (hit.object_type === "photo") {
    if (x.owner_label) bits.push(`on ${x.owner_label}`);
  } else if (hit.object_type === "alignment") {
    if (x.kind) bits.push(x.kind);
    if (Number.isFinite(x.length_units)) bits.push(`${Math.round(x.length_units)} ${x.units || "ft"}`);
  } else if (hit.object_type === "station_point") {
    if (x.alignment_name) bits.push(x.alignment_name);
    if (x.units) bits.push(x.units);
  } else if (hit.object_type === "pipe") {
    if (x.diameter_inches) bits.push(`${x.diameter_inches}"`);
    if (x.material) bits.push(x.material);
    if (Number.isFinite(x.length_units)) bits.push(`${Math.round(x.length_units)} ${x.units || "ft"}`);
  } else if (hit.object_type === "conflict") {
    if (x.kind) bits.push(x.kind);
    if (x.layer_a && x.layer_b) bits.push(`${x.layer_a} × ${x.layer_b}`);
  } else if (hit.object_type === "document") {
    if (x.document_type) bits.push(x.document_type.replace(/_/g, " ").toLowerCase());
    if (x.owner_label) bits.push(`on ${x.owner_label}`);
  }
  return bits.length ? `  ·  ${bits.join("  ·  ")}` : "";
}

/** Optional header search button.
 *  Lets any toolbar mount a `<SearchTrigger />` that opens the palette.
 *  Mobile/iPad-friendly (per directive §1 — visible icon/button). */
export function SearchTrigger({ compact = false }) {
  return (
    <button
      onClick={() => window.dispatchEvent(new CustomEvent("forgedops:open-search"))}
      data-testid="search-trigger"
      className={
        compact
          ? "h-11 w-11 flex items-center justify-center rounded-md text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] transition-colors"
          : "h-11 inline-flex items-center gap-2 px-4 rounded-md text-sm text-[var(--fop-text-2)] hover:text-[var(--fop-text)] bg-[var(--fop-surface-2)] hover:bg-[var(--fop-surface-3)] border border-[var(--fop-border)] transition-colors"
      }
      aria-label="Search"
    >
      <Search size={14} />
      {!compact && (
        <>
          <span className="hidden md:inline">Search</span>
          <kbd className="hidden md:inline text-[11px] font-mono uppercase tracking-wider px-1 py-0.5 rounded border border-[var(--fop-border)] text-[var(--fop-text-4)]">
            ⌘K
          </kbd>
        </>
      )}
    </button>
  );
}
